import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config/firebase';
import {
  StudentDemandRecord,
  CreateStudentDemandInput,
  UpdateStudentDemandInput,
  StudentDemandFilter,
  StudentDemandSummaryAnalytics,
  UniversityDemandAnalytics,
  AreaDemandAnalytics,
  UniversityAreaMatrixData,
  DemandPlanningSettings,
  DemandPlanningStatus,
  Weekday,
  RouteDemandBreakdown,
  PickupStationDemandBreakdown,
  DailyRoutePassengerBreakdown,
} from '../types';
import { getAlexandriaAreaById } from '../constants/alexandriaAreas';
import { SEED_UNIVERSITIES } from '../constants/mockSeed';
import { normalizeEgyptianPhone, validateEgyptianPhone } from '../utils/errors';
import { DEFAULT_LAUNCH_STATION, buildStandardRouteName } from '../constants/tripRoutes';
import { auditService } from './auditService';
import { universityService } from './universityService';

const LOCAL_DEMAND_KEY = 'oto_dev_student_demand_records';
const LOCAL_SETTINGS_KEY = 'oto_dev_demand_settings';

export const DEFAULT_DEMAND_SETTINGS: DemandPlanningSettings = {
  minStudentsForRoute: 10,
  highDemandThreshold: 25,
  veryHighDemandThreshold: 50,
  vehicleCapacity: 50, // Standard 50-Passenger Bus
};

export const DATE_TO_WEEKDAY: Record<number, Weekday> = {
  0: 'SUNDAY',
  1: 'MONDAY',
  2: 'TUESDAY',
  3: 'WEDNESDAY',
  4: 'THURSDAY',
  5: 'FRIDAY',
  6: 'SATURDAY',
};

export const WEEKDAY_ARABIC_NAMES: Record<Weekday, string> = {
  SATURDAY: 'السبت',
  SUNDAY: 'الأحد',
  MONDAY: 'الإثنين',
  TUESDAY: 'الثلاثاء',
  WEDNESDAY: 'الأربعاء',
  THURSDAY: 'الخميس',
  FRIDAY: 'الجمعة',
};

function getLocalDemandRecords(): StudentDemandRecord[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(LOCAL_DEMAND_KEY) || localStorage.getItem(LOCAL_DEMAND_KEY);
    if (!raw) {
      return [];
    }
    const parsed: StudentDemandRecord[] = JSON.parse(raw);
    // Purge any mock/dummy demo records from previous test phases
    const cleanRecords = Array.isArray(parsed)
      ? parsed.filter(
          (r) => r && !r.id?.startsWith('dmd_rec_') && r.studentName !== 'أحمد محمود إسماعيل'
        )
      : [];
    if (cleanRecords.length !== (Array.isArray(parsed) ? parsed.length : 0)) {
      saveLocalDemandRecords(cleanRecords);
    }
    return cleanRecords;
  } catch {
    return [];
  }
}

function saveLocalDemandRecords(records: StudentDemandRecord[]) {
  if (typeof window === 'undefined') return;
  try {
    const serialized = JSON.stringify(records);
    sessionStorage.setItem(LOCAL_DEMAND_KEY, serialized);
    localStorage.setItem(LOCAL_DEMAND_KEY, serialized);
  } catch {
    // Local storage error ignored
  }
}

export const studentDemandService = {
  /**
   * Get operational settings (thresholds, vehicle capacity)
   */
  async getSettings(): Promise<DemandPlanningSettings> {
    try {
      const snap = await getDoc(doc(db, 'settings', 'demandSettings'));
      if (snap.exists()) {
        return { ...DEFAULT_DEMAND_SETTINGS, ...snap.data() } as DemandPlanningSettings;
      }
    } catch {
      // Fallback to local
    }
    if (typeof window !== 'undefined') {
      try {
        const raw = localStorage.getItem(LOCAL_SETTINGS_KEY);
        if (raw) return JSON.parse(raw);
      } catch {
        // Ignored
      }
    }
    return { ...DEFAULT_DEMAND_SETTINGS };
  },

  /**
   * Update operational thresholds & vehicle capacity
   */
  async updateSettings(
    newSettings: Partial<DemandPlanningSettings>,
    adminUser: { uid: string; email: string }
  ): Promise<DemandPlanningSettings> {
    const current = await this.getSettings();
    const updated: DemandPlanningSettings = {
      ...current,
      ...newSettings,
      updatedAt: new Date().toISOString(),
      updatedBy: adminUser.email,
    };

    try {
      await setDoc(doc(db, 'settings', 'demandSettings'), updated, { merge: true });
    } catch {
      // Offline fallback
    }

    if (typeof window !== 'undefined') {
      localStorage.setItem(LOCAL_SETTINGS_KEY, JSON.stringify(updated));
    }

    await auditService.logAction(
      adminUser.uid,
      adminUser.email,
      'DEMAND_SETTINGS_UPDATE',
      'STUDENT_DEMAND',
      'demandSettings',
      { updated }
    );

    return updated;
  },

  /**
   * Check for duplicate student demand record (phone + universityId + ACTIVE)
   */
  async checkDuplicate(
    phone: string,
    universityId: string
  ): Promise<{ isDuplicate: boolean; existingRecord?: StudentDemandRecord }> {
    const normalized = normalizeEgyptianPhone(phone);
    if (!normalized || !universityId) return { isDuplicate: false };

    // 1. Check in Firestore
    try {
      const q = query(
        collection(db, 'studentDemand'),
        where('normalizedPhone', '==', normalized),
        where('universityId', '==', universityId),
        where('status', '==', 'ACTIVE')
      );
      const snap = await getDocs(q);
      if (!snap.empty) {
        const found = { ...snap.docs[0].data(), id: snap.docs[0].id } as StudentDemandRecord;
        return { isDuplicate: true, existingRecord: found };
      }
    } catch {
      // Fallback to local check
    }

    // 2. Local check
    const local = getLocalDemandRecords();
    const foundLocal = local.find(
      (r) => r.status === 'ACTIVE' && r.normalizedPhone === normalized && r.universityId === universityId
    );
    if (foundLocal) {
      return { isDuplicate: true, existingRecord: foundLocal };
    }

    return { isDuplicate: false };
  },

  /**
   * Create a new student demand record with validation, duplicate detection, and aggregation update
   */
  async createDemandRecord(
    input: CreateStudentDemandInput,
    adminUser: { uid: string; email: string; name?: string },
    options?: { skipDuplicateCheck?: boolean }
  ): Promise<{
    success: boolean;
    data?: StudentDemandRecord;
    duplicateFound?: boolean;
    existingRecord?: StudentDemandRecord;
    error?: string;
  }> {
    try {
      // 1. Validations
      const trimmedName = input.studentName?.trim() || '';
      if (!trimmedName || trimmedName.length < 2) {
        return { success: false, error: 'يرجى إدخال اسم الطالب بشكل صحيح (حرفين على الأقل)' };
      }

      if (!input.phone || !validateEgyptianPhone(input.phone)) {
        return {
          success: false,
          error: 'يرجى إدخال رقم هاتف مصري صحيح (11 رقم يبدأ بـ 010 أو 011 أو 012 أو 015)',
        };
      }
      const normalizedPhone = normalizeEgyptianPhone(input.phone);

      if (!input.universityId) {
        return { success: false, error: 'يرجى اختيار الجامعة المطلوبة' };
      }

      if (!input.areaId) {
        return { success: false, error: 'يرجى اختيار المنطقة السكنية للطالب' };
      }

      // Check attendance days validity if provided
      let attendanceDays: number | null = null;
      if (input.attendanceDaysPerMonth !== undefined && input.attendanceDaysPerMonth !== null && String(input.attendanceDaysPerMonth).trim() !== '') {
        const parsed = Number(input.attendanceDaysPerMonth);
        if (isNaN(parsed) || !Number.isInteger(parsed) || parsed < 1 || parsed > 31) {
          return {
            success: false,
            error: 'عدد أيام الحضور شهرياً يجب أن يكون عدداً صحيحاً بين 1 و 31 يوماً',
          };
        }
        attendanceDays = parsed;
      }

      // 2. Duplicate Check
      if (!options?.skipDuplicateCheck) {
        const dupCheck = await this.checkDuplicate(normalizedPhone, input.universityId);
        if (dupCheck.isDuplicate && dupCheck.existingRecord) {
          return {
            success: false,
            duplicateFound: true,
            existingRecord: dupCheck.existingRecord,
            error: 'هذا الطالب مسجل بالفعل لهذه الجامعة.',
          };
        }
      }

      // 3. Resolve metadata snapshots
      const area = getAlexandriaAreaById(input.areaId) || {
        id: input.areaId,
        name: input.areaName || 'منطقة أخرى',
        groupId: input.areaGroupId || 'group_other',
        groupName: input.areaGroupName || 'مناطق أخرى',
      };

      const uni = SEED_UNIVERSITIES.find((u) => u.id === input.universityId) || {
        id: input.universityId,
        name: input.universityName || 'جامعة غير محددة',
        governorate: input.universityGovernorate || 'غير محدد',
      };

      const nowIso = new Date().toISOString();
      const docRef = doc(collection(db, 'studentDemand'));

      const newRecord: StudentDemandRecord = {
        id: docRef.id,
        studentName: trimmedName,
        phone: input.phone.trim(),
        normalizedPhone,
        areaId: area.id,
        areaName: area.name,
        areaGroupId: area.groupId,
        areaGroupName: area.groupName,
        universityId: uni.id,
        universityNameSnapshot: uni.name,
        universityGovernorateNameSnapshot: uni.governorate,
        attendanceDaysPerMonth: attendanceDays,
        preferredTravelDays: input.preferredTravelDays || undefined,
        preferredDepartureTime: input.preferredDepartureTime?.trim() || undefined,
        preferredReturnTime: input.preferredReturnTime?.trim() || undefined,
        notes: input.notes?.trim() || undefined,
        status: 'ACTIVE',
        planningStatus: 'NEW',
        createdAt: nowIso,
        updatedAt: nowIso,
        createdBy: {
          uid: adminUser.uid,
          email: adminUser.email,
          name: adminUser.name || adminUser.email.split('@')[0],
        },
      };

      // 4. Persist to Firestore
      try {
        await setDoc(docRef, newRecord);
      } catch (firestoreErr) {
        console.warn('Could not write student demand to Firestore, persisting locally:', firestoreErr);
      }

      // 5. Persist to local dev storage
      const local = getLocalDemandRecords();
      local.unshift(newRecord);
      saveLocalDemandRecords(local);

      // 6. Audit Logging
      await auditService.logAction(
        adminUser.uid,
        adminUser.email,
        'DEMAND_RECORD_CREATE',
        'STUDENT_DEMAND',
        newRecord.id,
        {
          studentName: newRecord.studentName,
          phone: newRecord.phone,
          universityName: newRecord.universityNameSnapshot,
          areaName: newRecord.areaName,
          attendanceDays: newRecord.attendanceDaysPerMonth,
        }
      );

      return { success: true, data: newRecord };
    } catch (err: any) {
      console.error('Failed to create student demand record:', err);
      return { success: false, error: err.message || 'حدث خطأ غير متوقع أثناء تسجيل طلب الطالب' };
    }
  },

  /**
   * Automatically record or update demand for a student upon registration or profile completion
   * Ensures that all universities selected by students (from standard dropdown or unlisted) are captured in demand analytics.
   */
  async recordStudentProfileDemand(input: {
    studentId?: string;
    studentName: string;
    phone: string;
    universityId: string;
    universityName: string;
    universityGovernorate?: string;
    address?: string;
    assignedRoute?: string;
    pickupStation?: string;
    college?: string;
  }): Promise<{ success: boolean; data?: StudentDemandRecord; error?: string }> {
    try {
      const cleanName = input.studentName?.trim() || 'طالب مسجل';
      const cleanPhone = input.phone?.trim() || '';
      const normalizedPhone = normalizeEgyptianPhone(cleanPhone) || cleanPhone;

      if (!input.universityId || !input.universityName) {
        return { success: false, error: 'بيانات الجامعة غير مكتملة' };
      }

      // 1. Resolve Area from address keywords or default
      const addr = (input.address || '').toLowerCase();
      let matchedAreaId = 'area_sidi_gaber';
      if (addr.includes('عجمي') || addr.includes('دخيلة') || addr.includes('21') || addr.includes('عامرية')) {
        matchedAreaId = 'area_agami';
      } else if (addr.includes('سموحة')) {
        matchedAreaId = 'area_smouha';
      } else if (addr.includes('ميامي')) {
        matchedAreaId = 'area_miami';
      } else if (addr.includes('عصافرة') || addr.includes('مندرة')) {
        matchedAreaId = 'area_asafra';
      } else if (addr.includes('محرم بيك') || addr.includes('موقف')) {
        matchedAreaId = 'area_moharram_bek';
      }

      const area = getAlexandriaAreaById(matchedAreaId) || {
        id: matchedAreaId,
        name: input.address || 'الإسكندرية',
        groupId: 'group_east',
        groupName: 'شرق الإسكندرية',
      };

      // 2. Resolve University Snapshot
      const resolvedUni = SEED_UNIVERSITIES.find((u) => u.id === input.universityId) || {
        id: input.universityId,
        name: input.universityName,
        governorate: input.universityGovernorate || 'مصر',
      };

      const defaultStation = input.pickupStation || (matchedAreaId === 'area_agami' ? 'الكيلو 21' : DEFAULT_LAUNCH_STATION);
      const defaultRoute = input.assignedRoute || buildStandardRouteName(defaultStation, resolvedUni.name);

      const nowIso = new Date().toISOString();

      // 3. Check if demand record already exists for this student (by studentId or normalizedPhone)
      const existingList = await this.getAllDemandRecords();
      const existing = existingList.find(
        (r) => (input.studentId && r.studentId === input.studentId) || (normalizedPhone && r.normalizedPhone === normalizedPhone)
      );

      if (existing) {
        // Update existing record with the new university selection
        const updatedRecord: StudentDemandRecord = {
          ...existing,
          studentName: cleanName,
          phone: cleanPhone,
          normalizedPhone,
          universityId: resolvedUni.id,
          universityNameSnapshot: resolvedUni.name,
          universityGovernorateNameSnapshot: input.universityGovernorate || resolvedUni.governorate,
          assignedRoute: defaultRoute,
          pickupStation: defaultStation,
          areaId: area.id,
          areaName: input.address ? `${area.name} - ${input.address}` : area.name,
          areaGroupId: area.groupId,
          areaGroupName: area.groupName,
          college: input.college || existing.college,
          status: 'ACTIVE',
          updatedAt: nowIso,
          updatedBy: {
            uid: input.studentId || 'system',
            email: 'student-profile@otostudents.com',
          },
        };

        try {
          await setDoc(doc(db, 'studentDemand', existing.id), updatedRecord, { merge: true });
        } catch (e) {
          console.warn('Could not update student demand in Firestore:', e);
        }

        const local = getLocalDemandRecords().map((r) => (r.id === existing.id ? updatedRecord : r));
        saveLocalDemandRecords(local);

        return { success: true, data: updatedRecord };
      }

      // 4. Create new Demand Record
      const docRef = doc(collection(db, 'studentDemand'));
      const newRecord: StudentDemandRecord = {
        id: docRef.id,
        studentId: input.studentId,
        studentName: cleanName,
        phone: cleanPhone,
        normalizedPhone,
        areaId: area.id,
        areaName: input.address ? `${area.name} - ${input.address}` : area.name,
        areaGroupId: area.groupId,
        areaGroupName: area.groupName,
        universityId: resolvedUni.id,
        universityNameSnapshot: resolvedUni.name,
        universityGovernorateNameSnapshot: input.universityGovernorate || resolvedUni.governorate,
        attendanceDaysPerMonth: 16,
        preferredTravelDays: ['SATURDAY', 'SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY'],
        assignedRoute: defaultRoute,
        pickupStation: defaultStation,
        college: input.college,
        sourceType: 'REGISTERED_STUDENT',
        status: 'ACTIVE',
        planningStatus: 'NEW',
        createdAt: nowIso,
        updatedAt: nowIso,
        createdBy: {
          uid: input.studentId || 'system',
          email: 'student-profile@otostudents.com',
          name: 'تسجيل الطالب واختيار الجامعة',
        },
      };

      try {
        await setDoc(docRef, newRecord);
      } catch (firestoreErr) {
        console.warn('Could not write student demand to Firestore, persisting locally:', firestoreErr);
      }

      const local = getLocalDemandRecords();
      local.unshift(newRecord);
      saveLocalDemandRecords(local);

      return { success: true, data: newRecord };
    } catch (err: any) {
      console.error('Failed to record student profile demand:', err);
      return { success: false, error: err.message || 'حدث خطأ أثناء حفظ طلب الجامعة' };
    }
  },

  /**
   * Update an existing demand record
   */
  async updateDemandRecord(
    recordId: string,
    updates: UpdateStudentDemandInput,
    adminUser: { uid: string; email: string }
  ): Promise<{ success: boolean; data?: StudentDemandRecord; error?: string }> {
    try {
      const all = await this.getAllDemandRecords();
      const existing = all.find((r) => r.id === recordId);
      if (!existing) {
        return { success: false, error: 'سجل الطلب غير موجود في النظام' };
      }

      const nowIso = new Date().toISOString();
      const updatedRecord: StudentDemandRecord = {
        ...existing,
        studentName: updates.studentName !== undefined ? updates.studentName.trim() : existing.studentName,
        phone: updates.phone !== undefined ? updates.phone.trim() : existing.phone,
        normalizedPhone:
          updates.phone !== undefined ? normalizeEgyptianPhone(updates.phone) : existing.normalizedPhone,
        notes: updates.notes !== undefined ? updates.notes.trim() : existing.notes,
        attendanceDaysPerMonth:
          updates.attendanceDaysPerMonth !== undefined
            ? updates.attendanceDaysPerMonth
            : existing.attendanceDaysPerMonth,
        preferredTravelDays:
          updates.preferredTravelDays !== undefined
            ? updates.preferredTravelDays
            : existing.preferredTravelDays,
        planningStatus: updates.planningStatus || existing.planningStatus,
        updatedAt: nowIso,
        updatedBy: {
          uid: adminUser.uid,
          email: adminUser.email,
        },
      };

      if (updates.areaId && updates.areaId !== existing.areaId) {
        const area = getAlexandriaAreaById(updates.areaId);
        if (area) {
          updatedRecord.areaId = area.id;
          updatedRecord.areaName = area.name;
          updatedRecord.areaGroupId = area.groupId;
          updatedRecord.areaGroupName = area.groupName;
        }
      }

      if (updates.universityId && updates.universityId !== existing.universityId) {
        const uni = SEED_UNIVERSITIES.find((u) => u.id === updates.universityId);
        if (uni) {
          updatedRecord.universityId = uni.id;
          updatedRecord.universityNameSnapshot = uni.name;
          updatedRecord.universityGovernorateNameSnapshot = uni.governorate;
        }
      }

      try {
        await updateDoc(doc(db, 'studentDemand', recordId), updatedRecord as any);
      } catch {
        // Fallback to local
      }

      const local = getLocalDemandRecords().map((r) => (r.id === recordId ? updatedRecord : r));
      saveLocalDemandRecords(local);

      await auditService.logAction(
        adminUser.uid,
        adminUser.email,
        'DEMAND_RECORD_UPDATE',
        'STUDENT_DEMAND',
        recordId,
        { updates }
      );

      return { success: true, data: updatedRecord };
    } catch (err: any) {
      return { success: false, error: err.message || 'فشل تحديث بيانات الطلب' };
    }
  },

  /**
   * Soft archive demand record
   */
  async archiveDemandRecord(
    recordId: string,
    adminUser: { uid: string; email: string },
    reason?: string
  ): Promise<boolean> {
    try {
      const nowIso = new Date().toISOString();
      const patch = {
        status: 'ARCHIVED',
        archivedAt: nowIso,
        archivedBy: adminUser,
        archiveReason: reason || 'أرشفة إدارية',
        updatedAt: nowIso,
      };

      try {
        await updateDoc(doc(db, 'studentDemand', recordId), patch as any);
      } catch {
        // Ignored
      }

      const local = getLocalDemandRecords().map((r) =>
        r.id === recordId
          ? {
              ...r,
              status: 'ARCHIVED' as const,
              archivedAt: nowIso,
              archivedBy: adminUser,
              archiveReason: reason || 'أرشفة إدارية',
              updatedAt: nowIso,
            }
          : r
      );
      saveLocalDemandRecords(local);

      await auditService.logAction(
        adminUser.uid,
        adminUser.email,
        'DEMAND_RECORD_ARCHIVE',
        'STUDENT_DEMAND',
        recordId,
        { reason }
      );

      return true;
    } catch (err) {
      console.error('Failed to archive demand record:', err);
      return false;
    }
  },

  /**
   * Permanently delete demand record (strictly requires security password 984512)
   */
  async deleteDemandRecord(
    recordId: string,
    adminUser: { uid: string; email: string }
  ): Promise<boolean> {
    try {
      try {
        await deleteDoc(doc(db, 'studentDemand', recordId));
      } catch (err) {
        console.warn('Firestore deleteDoc fallback to local:', err);
      }

      const local = getLocalDemandRecords().filter((r) => r.id !== recordId);
      saveLocalDemandRecords(local);

      await auditService.logAction(
        adminUser.uid,
        adminUser.email,
        'DEMAND_RECORD_DELETE',
        'STUDENT_DEMAND',
        recordId
      );

      return true;
    } catch (err) {
      console.error('Failed to permanently delete demand record:', err);
      return false;
    }
  },

  /**
   * Update operational planning status (NEW, MONITORING, HIGH_DEMAND, etc.)
   */
  async updatePlanningStatus(
    recordId: string,
    planningStatus: DemandPlanningStatus,
    adminUser: { uid: string; email: string },
    note?: string
  ): Promise<boolean> {
    try {
      const nowIso = new Date().toISOString();
      const patch: any = {
        planningStatus,
        updatedAt: nowIso,
        updatedBy: adminUser,
      };
      if (note) {
        patch.notes = note;
      }

      try {
        await updateDoc(doc(db, 'studentDemand', recordId), patch);
      } catch {
        // Ignored
      }

      const local = getLocalDemandRecords().map((r) =>
        r.id === recordId ? { ...r, ...patch } : r
      );
      saveLocalDemandRecords(local);

      await auditService.logAction(
        adminUser.uid,
        adminUser.email,
        'DEMAND_PLANNING_STATUS_UPDATE',
        'STUDENT_DEMAND',
        recordId,
        { planningStatus, note }
      );

      return true;
    } catch (err) {
      console.error('Failed to update planning status:', err);
      return false;
    }
  },

  /**
   * Sync and link registered students & booking requests with Student Demand records
   * Links student travel requests with specific booked days, assigned routes, and pickup stations.
   */
  async syncAndLinkWithStudentsAndBookings(): Promise<{
    syncedStudentsCount: number;
    syncedBookingsCount: number;
    totalUpdated: number;
  }> {
    let syncedStudentsCount = 0;
    let syncedBookingsCount = 0;
    let totalUpdated = 0;

    try {
      // 1. Fetch Registered Students from 'students' collection
      const studentsMap = new Map<string, any>();
      const studentsPhoneMap = new Map<string, any>();
      try {
        const studentsSnap = await getDocs(collection(db, 'students'));
        studentsSnap.forEach((d) => {
          const data: any = { ...(d.data() as any), uid: d.id };
          studentsMap.set(d.id, data);
          if (data.phoneNumber) {
            studentsPhoneMap.set(normalizeEgyptianPhone(data.phoneNumber), data);
          }
        });
        syncedStudentsCount = studentsSnap.size;
      } catch (e) {
        console.warn('Could not read students collection for demand sync:', e);
      }

      // 2. Fetch Bookings and Booking Groups to extract travel days, dates, and pickup origins
      const studentBookingsMap = new Map<
        string,
        {
          dates: Set<string>;
          weekdays: Set<Weekday>;
          origins: Set<string>;
          routes: Set<string>;
          bookingCodes: Set<string>;
          universityName?: string;
          studentName?: string;
          phone?: string;
        }
      >();

      const addBookingInfo = (b: any) => {
        const phone = b.studentSnapshot?.phoneNumber || b.phoneNumber || '';
        const normPhone = phone ? normalizeEgyptianPhone(phone) : '';
        const studentId = b.studentId || b.studentSnapshot?.uid || normPhone || 'unknown';
        const key = normPhone || studentId;
        if (!key) return;

        if (!studentBookingsMap.has(key)) {
          studentBookingsMap.set(key, {
            dates: new Set(),
            weekdays: new Set(),
            origins: new Set(),
            routes: new Set(),
            bookingCodes: new Set(),
            universityName: b.universityName || b.studentSnapshot?.universityName,
            studentName: b.studentSnapshot?.fullName || b.studentName,
            phone: phone,
          });
        }

        const entry = studentBookingsMap.get(key)!;
        if (b.bookingCode) entry.bookingCodes.add(b.bookingCode);
        if (b.origin) entry.origins.add(b.origin);
        if (b.studentSnapshot?.assignedRoute) entry.routes.add(b.studentSnapshot.assignedRoute);

        // Extract dates and weekdays
        const datesToProcess: string[] = [];
        if (b.tripDate) datesToProcess.push(b.tripDate);
        if (b.tripDates && Array.isArray(b.tripDates)) {
          b.tripDates.forEach((td: string) => datesToProcess.push(td));
        }
        if (b.bookings && Array.isArray(b.bookings)) {
          b.bookings.forEach((sub: any) => {
            if (sub.date) datesToProcess.push(sub.date);
            if (sub.origin) entry.origins.add(sub.origin);
            if (sub.bookingCode) entry.bookingCodes.add(sub.bookingCode);
          });
        }

        datesToProcess.forEach((dt) => {
          if (dt && typeof dt === 'string') {
            entry.dates.add(dt);
            try {
              const dayIdx = new Date(dt).getDay();
              const wd = DATE_TO_WEEKDAY[dayIdx];
              if (wd) entry.weekdays.add(wd);
            } catch (_) {}
          }
        });
      };

      try {
        const bookingsSnap = await getDocs(collection(db, 'bookings'));
        bookingsSnap.forEach((d) => addBookingInfo(d.data()));
        const groupsSnap = await getDocs(collection(db, 'bookingGroups'));
        groupsSnap.forEach((d) => addBookingInfo(d.data()));
        syncedBookingsCount = bookingsSnap.size + groupsSnap.size;
      } catch (e) {
        console.warn('Could not read bookings for demand sync:', e);
      }

      // 3. Load existing demand records
      const existingList = await this.getAllDemandRecords();
      const existingPhoneMap = new Map<string, StudentDemandRecord>();
      existingList.forEach((r) => {
        existingPhoneMap.set(r.normalizedPhone, r);
        if (r.studentId) existingPhoneMap.set(r.studentId, r);
      });

      const nowIso = new Date().toISOString();

      // 4. Update existing records with registered student routes and bookings
      for (const rec of existingList) {
        let changed = false;
        const normPhone = rec.normalizedPhone;
        const regStudent = (rec.studentId && studentsMap.get(rec.studentId)) || (normPhone && studentsPhoneMap.get(normPhone));

        if (regStudent) {
          if (!rec.studentId) {
            rec.studentId = regStudent.uid;
            changed = true;
          }
          if (regStudent.universityId && rec.universityId !== regStudent.universityId) {
            rec.universityId = regStudent.universityId;
            rec.universityNameSnapshot = regStudent.universityName || rec.universityNameSnapshot;
            rec.universityGovernorateNameSnapshot = regStudent.universityGovernorate || rec.universityGovernorateNameSnapshot;
            changed = true;
          } else if (regStudent.universityName && rec.universityNameSnapshot !== regStudent.universityName) {
            rec.universityNameSnapshot = regStudent.universityName;
            if (regStudent.universityGovernorate) {
              rec.universityGovernorateNameSnapshot = regStudent.universityGovernorate;
            }
            changed = true;
          }
          if (regStudent.assignedRoute && (!rec.assignedRoute || rec.assignedRoute === 'غير محدد')) {
            rec.assignedRoute = regStudent.assignedRoute;
            changed = true;
          }
          if (regStudent.defaultPickupStation && (!rec.pickupStation || rec.pickupStation === 'غير محدد')) {
            rec.pickupStation = regStudent.defaultPickupStation;
            changed = true;
          }
          if (regStudent.college && !rec.college) {
            rec.college = regStudent.college;
            changed = true;
          }
        }

        // Link bookings
        const bookingData = studentBookingsMap.get(normPhone) || (rec.studentId && studentBookingsMap.get(rec.studentId));
        if (bookingData) {
          if (bookingData.weekdays.size > 0) {
            const mergedDays = Array.from(new Set([...(rec.preferredTravelDays || []), ...Array.from(bookingData.weekdays)]));
            rec.preferredTravelDays = mergedDays;
            changed = true;
          }
          if (bookingData.dates.size > 0) {
            rec.specificTravelDates = Array.from(bookingData.dates);
            changed = true;
          }
          if (bookingData.bookingCodes.size > 0) {
            rec.bookingCodes = Array.from(bookingData.bookingCodes);
            changed = true;
          }
          if (bookingData.origins.size > 0 && (!rec.pickupStation || rec.pickupStation === 'غير محدد')) {
            rec.pickupStation = Array.from(bookingData.origins).join(' / ');
            changed = true;
          }
          if (bookingData.routes.size > 0 && (!rec.assignedRoute || rec.assignedRoute === 'غير محدد')) {
            rec.assignedRoute = Array.from(bookingData.routes)[0];
            changed = true;
          }
          rec.sourceType = 'BOOKING';
        } else if (regStudent && !rec.sourceType) {
          rec.sourceType = 'REGISTERED_STUDENT';
        }

        // Default route fallback if still missing (Strictly from standard launch station, NOT geographic area)
        if (!rec.assignedRoute) {
          rec.assignedRoute = buildStandardRouteName(DEFAULT_LAUNCH_STATION, rec.universityNameSnapshot);
          changed = true;
        }
        if (!rec.pickupStation) {
          rec.pickupStation = DEFAULT_LAUNCH_STATION;
          changed = true;
        }

        if (changed) {
          totalUpdated++;
          rec.updatedAt = nowIso;
          try {
            await setDoc(doc(db, 'studentDemand', rec.id), rec, { merge: true });
          } catch (_) {}
        }
      }

      // 5. Create Demand Records for Registered Students who don't have a record yet
      for (const [uid, student] of studentsMap.entries()) {
        const normPhone = student.phoneNumber ? normalizeEgyptianPhone(student.phoneNumber) : '';
        if (normPhone && !existingPhoneMap.has(normPhone) && !existingPhoneMap.has(uid)) {
          const bookingData = studentBookingsMap.get(normPhone) || studentBookingsMap.get(uid);
          const travelDays = bookingData?.weekdays.size
            ? Array.from(bookingData.weekdays)
            : (['SATURDAY', 'SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY'] as Weekday[]);

          const targetUniId = student.universityId || 'uni_general';
          const seedUni = SEED_UNIVERSITIES.find((u) => u.id === targetUniId || u.name === student.universityName);
          const resolvedUniId = seedUni ? seedUni.id : targetUniId;
          const resolvedUniName = student.universityName || seedUni?.name || 'جامعة غير محددة';
          const resolvedGov = student.universityGovernorate || seedUni?.governorate || 'مصر';

          const docRef = doc(collection(db, 'studentDemand'));
          const area = getAlexandriaAreaById('area_sidi_gaber') || {
            id: 'area_custom',
            name: student.address || 'الإسكندرية',
            groupId: 'group_east',
            groupName: 'شرق الإسكندرية',
          };

          const newRec: StudentDemandRecord = {
            id: docRef.id,
            studentId: uid,
            studentName: student.fullName || 'طالب مسجل',
            phone: student.phoneNumber || '',
            normalizedPhone: normPhone,
            college: student.college || '',
            assignedRoute: student.assignedRoute || (bookingData?.routes.size ? Array.from(bookingData.routes)[0] : buildStandardRouteName(DEFAULT_LAUNCH_STATION, resolvedUniName)),
            pickupStation: student.defaultPickupStation || (bookingData?.origins.size ? Array.from(bookingData.origins)[0] : DEFAULT_LAUNCH_STATION),
            areaId: area.id,
            areaName: student.address || area.name,
            areaGroupId: area.groupId,
            areaGroupName: area.groupName,
            universityId: resolvedUniId,
            universityNameSnapshot: resolvedUniName,
            universityGovernorateNameSnapshot: resolvedGov,
            attendanceDaysPerMonth: bookingData?.dates.size ? bookingData.dates.size * 4 : 16,
            preferredTravelDays: travelDays,
            specificTravelDates: bookingData?.dates.size ? Array.from(bookingData.dates) : undefined,
            bookingCodes: bookingData?.bookingCodes.size ? Array.from(bookingData.bookingCodes) : undefined,
            sourceType: bookingData ? 'BOOKING' : 'REGISTERED_STUDENT',
            status: 'ACTIVE',
            planningStatus: 'NEW',
            createdAt: nowIso,
            updatedAt: nowIso,
            createdBy: {
              uid: 'system',
              email: 'system@otostudents.com',
              name: 'مزامنة الطلاب المسجلين والحجوزات',
            },
          };

          try {
            await setDoc(docRef, newRec, { merge: true });
            existingList.unshift(newRec);
            totalUpdated++;
          } catch (_) {}
        }
      }

      saveLocalDemandRecords(existingList);
    } catch (err) {
      console.error('Error during demand sync with students and bookings:', err);
    }

    return {
      syncedStudentsCount,
      syncedBookingsCount,
      totalUpdated,
    };
  },

  /**
   * Retrieve all demand records (merging Firestore & dev local)
   */
  async getAllDemandRecords(): Promise<StudentDemandRecord[]> {
    const list: StudentDemandRecord[] = [];
    try {
      const q = query(collection(db, 'studentDemand'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      snap.forEach((d) => {
        list.push({ ...d.data(), id: d.id } as StudentDemandRecord);
      });
    } catch {
      // Ignored
    }

    const local = getLocalDemandRecords();
    for (const item of local) {
      if (!list.some((existing) => existing.id === item.id)) {
        list.push(item);
      }
    }

    // Default route and station enrichment strictly from standard launch stations, not student's geographic address
    for (const item of list) {
      if (!item.assignedRoute) {
        item.assignedRoute = buildStandardRouteName(DEFAULT_LAUNCH_STATION, item.universityNameSnapshot || 'الجامعة');
      }
      if (!item.pickupStation) {
        item.pickupStation = DEFAULT_LAUNCH_STATION;
      }
    }

    return list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  },

  /**
   * Query records with filters & pagination
   */
  async queryDemandRecords(
    filter: StudentDemandFilter = {},
    page: number = 1,
    pageSize: number = 20
  ): Promise<{
    records: StudentDemandRecord[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
  }> {
    const all = await this.getAllDemandRecords();

    const filtered = all.filter((r) => {
      // Search
      if (filter.search && filter.search.trim()) {
        const s = filter.search.trim().toLowerCase();
        const matchName = r.studentName.toLowerCase().includes(s);
        const matchPhone = r.phone.includes(s) || r.normalizedPhone.includes(s);
        const matchUni = r.universityNameSnapshot.toLowerCase().includes(s);
        const matchArea = r.areaName.toLowerCase().includes(s) || r.areaGroupName.toLowerCase().includes(s);
        const matchNotes = r.notes?.toLowerCase().includes(s) || false;
        const matchRoute = r.assignedRoute?.toLowerCase().includes(s) || false;
        const matchPickup = r.pickupStation?.toLowerCase().includes(s) || false;
        const matchCollege = r.college?.toLowerCase().includes(s) || false;
        if (!matchName && !matchPhone && !matchUni && !matchArea && !matchNotes && !matchRoute && !matchPickup && !matchCollege) {
          return false;
        }
      }

      // Status
      if (filter.status && filter.status !== 'ALL') {
        if (r.status !== filter.status) return false;
      }

      // Planning Status
      if (filter.planningStatus && filter.planningStatus !== 'ALL') {
        if (r.planningStatus !== filter.planningStatus) return false;
      }

      // University
      if (filter.universityId && filter.universityId !== 'ALL') {
        if (r.universityId !== filter.universityId) return false;
      }

      // Governorate
      if (filter.governorate && filter.governorate !== 'ALL') {
        if (r.universityGovernorateNameSnapshot !== filter.governorate) return false;
      }

      // Area Group
      if (filter.areaGroupId && filter.areaGroupId !== 'ALL') {
        if (r.areaGroupId !== filter.areaGroupId) return false;
      }

      // Area
      if (filter.areaId && filter.areaId !== 'ALL') {
        if (r.areaId !== filter.areaId) return false;
      }

      // Assigned Route Filter
      if (filter.assignedRoute && filter.assignedRoute !== 'ALL') {
        if (r.assignedRoute !== filter.assignedRoute) return false;
      }

      // Pickup Station Filter
      if (filter.pickupStation && filter.pickupStation !== 'ALL') {
        if (r.pickupStation !== filter.pickupStation) return false;
      }

      // Attendance range
      if (filter.attendanceRange && filter.attendanceRange !== 'ALL') {
        const days = r.attendanceDaysPerMonth || 0;
        if (filter.attendanceRange === '1-5' && (days < 1 || days > 5)) return false;
        if (filter.attendanceRange === '6-10' && (days < 6 || days > 10)) return false;
        if (filter.attendanceRange === '11-15' && (days < 11 || days > 15)) return false;
        if (filter.attendanceRange === '16-20' && (days < 16 || days > 20)) return false;
        if (filter.attendanceRange === '21+' && days < 21) return false;
      }

      // Travel Day of the Week filter
      if (filter.travelDay && filter.travelDay !== 'ALL') {
        if (!r.preferredTravelDays || !r.preferredTravelDays.includes(filter.travelDay as any)) {
          return false;
        }
      }

      // Date Range
      if (filter.dateRange && filter.dateRange !== 'ALL') {
        const recordTime = new Date(r.createdAt).getTime();
        const now = Date.now();
        if (filter.dateRange === 'TODAY') {
          const startOfDay = new Date().setHours(0, 0, 0, 0);
          if (recordTime < startOfDay) return false;
        } else if (filter.dateRange === 'WEEK') {
          if (recordTime < now - 86400000 * 7) return false;
        } else if (filter.dateRange === 'MONTH') {
          if (recordTime < now - 86400000 * 30) return false;
        } else if (filter.dateRange === 'CUSTOM') {
          if (filter.customStartDate && recordTime < new Date(filter.customStartDate).getTime()) return false;
          if (filter.customEndDate && recordTime > new Date(filter.customEndDate).getTime() + 86400000) return false;
        }
      }

      return true;
    });

    const total = filtered.length;
    const totalPages = Math.ceil(total / pageSize) || 1;
    const clampedPage = Math.max(1, Math.min(page, totalPages));
    const startIndex = (clampedPage - 1) * pageSize;
    const records = filtered.slice(startIndex, startIndex + pageSize);

    return {
      records,
      total,
      page: clampedPage,
      pageSize,
      totalPages,
    };
  },

  /**
   * Compute comprehensive dashboard KPIs & summary analytics
   */
  async getSummaryAnalytics(): Promise<StudentDemandSummaryAnalytics> {
    const all = await this.getAllDemandRecords();
    const active = all.filter((r) => r.status === 'ACTIVE');

    let totalAttendanceDays = 0;
    let attendanceCount = 0;
    const uniMap: Record<string, { id: string; name: string; count: number }> = {};
    const areaMap: Record<string, { id: string; name: string; count: number }> = {};
    const govBreakdown: Record<string, number> = {};
    const areaGroupBreakdown: Record<string, number> = {};
    const weekdayBreakdown: Record<string, number> = {
      SATURDAY: 0,
      SUNDAY: 0,
      MONDAY: 0,
      TUESDAY: 0,
      WEDNESDAY: 0,
      THURSDAY: 0,
      FRIDAY: 0,
    };

    // Route & Pickup Station mappings
    const routeMap: Record<string, { count: number; stations: Record<string, number> }> = {};
    const stationMap: Record<string, { count: number; routes: Set<string> }> = {};
    const dailyRouteMap: Record<Weekday, Record<string, { route: string; pickupStation: string; count: number; studentNames: string[] }>> = {
      SATURDAY: {},
      SUNDAY: {},
      MONDAY: {},
      TUESDAY: {},
      WEDNESDAY: {},
      THURSDAY: {},
      FRIDAY: {},
    };

    active.forEach((r) => {
      // Attendance
      if (r.attendanceDaysPerMonth) {
        totalAttendanceDays += r.attendanceDaysPerMonth;
        attendanceCount += 1;
      }

      // Weekdays
      if (r.preferredTravelDays && Array.isArray(r.preferredTravelDays)) {
        r.preferredTravelDays.forEach((day) => {
          if (weekdayBreakdown[day] !== undefined) {
            weekdayBreakdown[day] += 1;
          }
        });
      }

      // Route & Station Breakdowns strictly using assigned route or standard launch station (not geographic residential area)
      const route = r.assignedRoute || buildStandardRouteName(DEFAULT_LAUNCH_STATION, r.universityNameSnapshot || 'الجامعة');
      const station = r.pickupStation || DEFAULT_LAUNCH_STATION;

      // Global Route
      if (!routeMap[route]) {
        routeMap[route] = { count: 0, stations: {} };
      }
      routeMap[route].count += 1;
      routeMap[route].stations[station] = (routeMap[route].stations[station] || 0) + 1;

      // Global Station
      if (!stationMap[station]) {
        stationMap[station] = { count: 0, routes: new Set() };
      }
      stationMap[station].count += 1;
      stationMap[station].routes.add(route);

      // Daily Breakdown
      const days = (r.preferredTravelDays && r.preferredTravelDays.length > 0)
        ? r.preferredTravelDays
        : (['SATURDAY', 'SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY'] as Weekday[]);

      days.forEach((day) => {
        if (dailyRouteMap[day]) {
          const comboKey = `${route}__${station}`;
          if (!dailyRouteMap[day][comboKey]) {
            dailyRouteMap[day][comboKey] = {
              route,
              pickupStation: station,
              count: 0,
              studentNames: [],
            };
          }
          dailyRouteMap[day][comboKey].count += 1;
          if (r.studentName && !dailyRouteMap[day][comboKey].studentNames.includes(r.studentName)) {
            dailyRouteMap[day][comboKey].studentNames.push(r.studentName);
          }
        }
      });

      // University counts
      if (!uniMap[r.universityId]) {
        uniMap[r.universityId] = { id: r.universityId, name: r.universityNameSnapshot, count: 0 };
      }
      uniMap[r.universityId].count += 1;

      // Area counts
      if (!areaMap[r.areaId]) {
        areaMap[r.areaId] = { id: r.areaId, name: r.areaName, count: 0 };
      }
      areaMap[r.areaId].count += 1;

      // Governorate breakdown
      const gov = r.universityGovernorateNameSnapshot || 'غير محدد';
      govBreakdown[gov] = (govBreakdown[gov] || 0) + 1;

      // Area Group breakdown
      const group = r.areaGroupName || 'غير مصنف';
      areaGroupBreakdown[group] = (areaGroupBreakdown[group] || 0) + 1;
    });

    // Highest demand university
    let highestUni: { universityId: string; universityName: string; count: number } | null = null;
    Object.values(uniMap).forEach((u) => {
      if (!highestUni || u.count > highestUni.count) {
        highestUni = { universityId: u.id, universityName: u.name, count: u.count };
      }
    });

    // Highest demand area
    let highestArea: { areaId: string; areaName: string; count: number } | null = null;
    Object.values(areaMap).forEach((a) => {
      if (!highestArea || a.count > highestArea.count) {
        highestArea = { areaId: a.id, areaName: a.name, count: a.count };
      }
    });

    // Opportunities count (universities with >= 10 students)
    const settings = await this.getSettings();
    const highOpportunities = Object.values(uniMap).filter(
      (u) => u.count >= settings.minStudentsForRoute
    ).length;

    // Monthly growth trend (last 4 months)
    const monthlyMap: Record<string, { count: number; attendance: number }> = {};
    active.forEach((r) => {
      const d = new Date(r.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyMap[key]) {
        monthlyMap[key] = { count: 0, attendance: 0 };
      }
      monthlyMap[key].count += 1;
      monthlyMap[key].attendance += r.attendanceDaysPerMonth || 16;
    });

    const monthNames: Record<string, string> = {
      '01': 'يناير',
      '02': 'فبراير',
      '03': 'مارس',
      '04': 'أبريل',
      '05': 'مايو',
      '06': 'يونيو',
      '07': 'يوليو',
      '08': 'أغسطس',
      '09': 'سبتمبر',
      '10': 'أكتوبر',
      '11': 'نوفمبر',
      '12': 'ديسمبر',
    };

    const monthlyGrowthTrend = Object.keys(monthlyMap)
      .sort()
      .map((key) => {
        const [y, m] = key.split('-');
        return {
          monthKey: key,
          monthLabel: `${monthNames[m] || m} ${y}`,
          studentCount: monthlyMap[key].count,
          attendanceDemand: monthlyMap[key].attendance,
        };
      });

    const avgAttendance = attendanceCount > 0 ? Number((totalAttendanceDays / attendanceCount).toFixed(1)) : 16.0;

    // Compile Route and Pickup Station Breakdown Arrays
    const routeBreakdown: RouteDemandBreakdown[] = Object.entries(routeMap)
      .map(([route, data]) => ({
        route,
        studentCount: data.count,
        percentage: active.length > 0 ? Math.round((data.count / active.length) * 100) : 0,
        pickupStations: Object.entries(data.stations).map(([station, count]) => ({ station, count })),
      }))
      .sort((a, b) => b.studentCount - a.studentCount);

    const pickupStationBreakdown: PickupStationDemandBreakdown[] = Object.entries(stationMap)
      .map(([station, data]) => ({
        station,
        studentCount: data.count,
        percentage: active.length > 0 ? Math.round((data.count / active.length) * 100) : 0,
        routes: Array.from(data.routes),
      }))
      .sort((a, b) => b.studentCount - a.studentCount);

    const dailyRouteBreakdown: Record<Weekday, DailyRoutePassengerBreakdown> = {} as any;
    (Object.keys(dailyRouteMap) as Weekday[]).forEach((day) => {
      const routesList = Object.values(dailyRouteMap[day])
        .map((item) => ({
          route: item.route,
          pickupStation: item.pickupStation,
          studentCount: item.count,
          studentNames: item.studentNames,
        }))
        .sort((a, b) => b.studentCount - a.studentCount);
      const totalStudents = routesList.reduce((acc, curr) => acc + curr.studentCount, 0);
      dailyRouteBreakdown[day] = {
        day,
        dayLabelAr: WEEKDAY_ARABIC_NAMES[day] || day,
        totalStudents,
        routes: routesList,
      };
    });

    return {
      totalDemandStudents: all.length,
      activeDemandStudents: active.length,
      archivedDemandStudents: all.length - active.length,
      totalAttendanceDemandDays: totalAttendanceDays,
      avgAttendanceDays: avgAttendance,
      uniqueUniversitiesRequested: Object.keys(uniMap).length,
      uniqueAreasRepresented: Object.keys(areaMap).length,
      highestDemandUniversity: highestUni,
      highestDemandArea: highestArea,
      highOpportunityRoutesCount: highOpportunities,
      governorateBreakdown: govBreakdown,
      areaGroupBreakdown,
      weekdayDemandBreakdown: weekdayBreakdown as Record<any, number>,
      routeBreakdown,
      pickupStationBreakdown,
      dailyRouteBreakdown,
      monthlyGrowthTrend,
      lastUpdated: new Date().toISOString(),
    };
  },

  /**
   * Analytics breakdown by University (with attendance demand & opportunity scores)
   */
  async getUniversityDemandAnalytics(): Promise<UniversityDemandAnalytics[]> {
    const all = await this.getAllDemandRecords();
    const settings = await this.getSettings();

    // 1. Collect all registered universities from SEED_UNIVERSITIES and Firestore
    const allRegisteredUnisMap = new Map<string, { id: string; name: string; governorate: string }>();
    SEED_UNIVERSITIES.forEach((u) => {
      allRegisteredUnisMap.set(u.id, {
        id: u.id,
        name: u.name,
        governorate: u.governorate || 'مصر',
      });
    });
    try {
      const firestoreUnis = await universityService.getAllUniversities();
      firestoreUnis.forEach((u) => {
        allRegisteredUnisMap.set(u.id, {
          id: u.id,
          name: u.name,
          governorate: u.governorate || 'مصر',
        });
      });
    } catch (_) {
      // Ignored
    }

    const uniMap: Record<
      string,
      {
        universityId: string;
        universityName: string;
        governorate: string;
        totalStudents: number;
        activeStudents: number;
        attendanceDaysSum: number;
        attendanceCount: number;
        areaCounts: Record<string, { count: number; attendance: number; areaName: string; groupName: string }>;
        weekdayCounts: Record<string, number>;
        planningStatus: DemandPlanningStatus;
      }
    > = {};

    // 2. Pre-populate uniMap with every registered university (guarantees all universities from registration dropdown are present)
    for (const [id, uni] of allRegisteredUnisMap.entries()) {
      uniMap[id] = {
        universityId: uni.id,
        universityName: uni.name,
        governorate: uni.governorate,
        totalStudents: 0,
        activeStudents: 0,
        attendanceDaysSum: 0,
        attendanceCount: 0,
        areaCounts: {},
        weekdayCounts: {
          SATURDAY: 0,
          SUNDAY: 0,
          MONDAY: 0,
          TUESDAY: 0,
          WEDNESDAY: 0,
          THURSDAY: 0,
          FRIDAY: 0,
        },
        planningStatus: 'MONITORING',
      };
    }

    // Helper to find matching university key (by ID first, then by exact or normalized name)
    const findUniKey = (uniId: string, uniName: string): string => {
      if (uniMap[uniId]) return uniId;
      const cleanName = (uniName || '').trim().toLowerCase();
      if (cleanName) {
        for (const [k, v] of Object.entries(uniMap)) {
          if (v.universityName.trim().toLowerCase() === cleanName) {
            return k;
          }
        }
      }
      return uniId;
    };

    all.forEach((r) => {
      const targetKey = findUniKey(r.universityId, r.universityNameSnapshot);
      if (!uniMap[targetKey]) {
        uniMap[targetKey] = {
          universityId: r.universityId,
          universityName: r.universityNameSnapshot || 'جامعة أخرى',
          governorate: r.universityGovernorateNameSnapshot || 'غير محدد',
          totalStudents: 0,
          activeStudents: 0,
          attendanceDaysSum: 0,
          attendanceCount: 0,
          areaCounts: {},
          weekdayCounts: {
            SATURDAY: 0,
            SUNDAY: 0,
            MONDAY: 0,
            TUESDAY: 0,
            WEDNESDAY: 0,
            THURSDAY: 0,
            FRIDAY: 0,
          },
          planningStatus: r.planningStatus || 'NEW',
        };
      }
      const u = uniMap[targetKey];
      u.totalStudents += 1;
      if (r.status === 'ACTIVE') {
        u.activeStudents += 1;
        const days = r.attendanceDaysPerMonth || 16;
        u.attendanceDaysSum += days;
        u.attendanceCount += 1;

        if (r.preferredTravelDays && Array.isArray(r.preferredTravelDays)) {
          r.preferredTravelDays.forEach((day) => {
            if (u.weekdayCounts[day] !== undefined) {
              u.weekdayCounts[day] += 1;
            }
          });
        }

        if (!u.areaCounts[r.areaId]) {
          u.areaCounts[r.areaId] = {
            count: 0,
            attendance: 0,
            areaName: r.areaName,
            groupName: r.areaGroupName,
          };
        }
        u.areaCounts[r.areaId].count += 1;
        u.areaCounts[r.areaId].attendance += days;

        // If high demand status exists, prioritize it
        if (r.planningStatus === 'ROUTE_PLANNING' || r.planningStatus === 'HIGH_DEMAND') {
          u.planningStatus = r.planningStatus;
        }
      }
    });

    const result: UniversityDemandAnalytics[] = Object.values(uniMap).map((u) => {
      const avgAttendance = u.attendanceCount > 0 ? Number((u.attendanceDaysSum / u.attendanceCount).toFixed(1)) : 16.0;
      const totalAttendanceDemand = u.activeStudents * avgAttendance;

      // Top Areas
      const topAreas = Object.entries(u.areaCounts)
        .map(([areaId, data]) => ({
          areaId,
          areaName: data.areaName,
          areaGroupName: data.groupName,
          studentCount: data.count,
          attendanceDemand: Math.round(data.attendance),
          percentage: u.activeStudents > 0 ? Math.round((data.count / u.activeStudents) * 100) : 0,
        }))
        .sort((a, b) => b.studentCount - a.studentCount);

      // Operations Score Calculation
      let operationsScore: 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH' = 'LOW';
      let recommendation = 'الطلب منخفض حالياً — استمرار المراقبة وتجميع رغبات الطلاب.';

      if (u.activeStudents >= settings.veryHighDemandThreshold) {
        operationsScore = 'VERY_HIGH';
        recommendation = 'إقبال مرتفع جداً! يوصى بالتشغيل الفوري لعدة سيارات وجدولة خط منتظم.';
      } else if (u.activeStudents >= settings.highDemandThreshold) {
        operationsScore = 'HIGH';
        recommendation = 'إقبال ممتاز يقترب من تشغيل حافلتين. يوصى ببدء ترتيب نقاط التجمع وتحديد المواعيد.';
      } else if (u.activeStudents >= settings.minStudentsForRoute) {
        operationsScore = 'MEDIUM';
        recommendation = 'اكتمال النصاب الأدنى لحافلة (50 مقعداً). جاهزة لتقييم الجدوى والجدولة.';
      }

      // HiAce capacity equivalent
      const hiaceEquivalent = Number((u.activeStudents / settings.vehicleCapacity).toFixed(1));

      return {
        universityId: u.universityId,
        universityName: u.universityName,
        governorate: u.governorate,
        totalStudents: u.totalStudents,
        activeStudents: u.activeStudents,
        totalAttendanceDemand: Math.round(totalAttendanceDemand),
        avgAttendanceDays: avgAttendance,
        topAreas,
        planningStatus: u.planningStatus,
        operationsScore,
        hiaceEquivalent,
        recommendation,
        weekdayDemandBreakdown: u.weekdayCounts as Record<any, number>,
        lastUpdated: new Date().toISOString(),
      };
    });

    return result.sort((a, b) => {
      if (b.activeStudents !== a.activeStudents) {
        return b.activeStudents - a.activeStudents;
      }
      if (b.totalStudents !== a.totalStudents) {
        return b.totalStudents - a.totalStudents;
      }
      return a.universityName.localeCompare(b.universityName, 'ar');
    });
  },

  /**
   * Analytics breakdown by Alexandria Residential Area
   */
  async getAreaDemandAnalytics(): Promise<AreaDemandAnalytics[]> {
    const all = await this.getAllDemandRecords();

    const areaMap: Record<
      string,
      {
        areaId: string;
        areaName: string;
        areaGroupId: string;
        areaGroupName: string;
        totalStudents: number;
        activeStudents: number;
        attendanceDaysSum: number;
        attendanceCount: number;
        uniCounts: Record<string, { count: number; attendance: number; uniName: string; gov: string }>;
      }
    > = {};

    all.forEach((r) => {
      if (!areaMap[r.areaId]) {
        areaMap[r.areaId] = {
          areaId: r.areaId,
          areaName: r.areaName,
          areaGroupId: r.areaGroupId,
          areaGroupName: r.areaGroupName,
          totalStudents: 0,
          activeStudents: 0,
          attendanceDaysSum: 0,
          attendanceCount: 0,
          uniCounts: {},
        };
      }
      const a = areaMap[r.areaId];
      a.totalStudents += 1;
      if (r.status === 'ACTIVE') {
        a.activeStudents += 1;
        const days = r.attendanceDaysPerMonth || 16;
        a.attendanceDaysSum += days;
        a.attendanceCount += 1;

        if (!a.uniCounts[r.universityId]) {
          a.uniCounts[r.universityId] = {
            count: 0,
            attendance: 0,
            uniName: r.universityNameSnapshot,
            gov: r.universityGovernorateNameSnapshot,
          };
        }
        a.uniCounts[r.universityId].count += 1;
        a.uniCounts[r.universityId].attendance += days;
      }
    });

    const result: AreaDemandAnalytics[] = Object.values(areaMap).map((a) => {
      const avgAttendance = a.attendanceCount > 0 ? Number((a.attendanceDaysSum / a.attendanceCount).toFixed(1)) : 16.0;
      const totalAttendanceDemand = a.activeStudents * avgAttendance;

      const topUniversities = Object.entries(a.uniCounts)
        .map(([uniId, data]) => ({
          universityId: uniId,
          universityName: data.uniName,
          governorate: data.gov,
          studentCount: data.count,
          attendanceDemand: Math.round(data.attendance),
          percentage: a.activeStudents > 0 ? Math.round((data.count / a.activeStudents) * 100) : 0,
        }))
        .sort((x, y) => y.studentCount - x.studentCount);

      return {
        areaId: a.areaId,
        areaName: a.areaName,
        areaGroupId: a.areaGroupId,
        areaGroupName: a.areaGroupName,
        totalStudents: a.totalStudents,
        activeStudents: a.activeStudents,
        totalAttendanceDemand: Math.round(totalAttendanceDemand),
        avgAttendanceDays: avgAttendance,
        topUniversities,
        lastUpdated: new Date().toISOString(),
      };
    });

    return result.sort((a, b) => b.activeStudents - a.activeStudents);
  },

  /**
   * Generates the Residential Area × University Heatmap / Matrix
   */
  async getUniversityAreaMatrix(): Promise<UniversityAreaMatrixData> {
    const all = await this.getAllDemandRecords();
    const active = all.filter((r) => r.status === 'ACTIVE');

    const areaTotals: Record<string, { areaId: string; areaName: string; areaGroupName: string; total: number }> = {};
    const uniTotals: Record<string, { universityId: string; universityName: string; governorate: string; total: number }> = {};
    const cells: UniversityAreaMatrixData['cells'] = {};

    active.forEach((r) => {
      // Area total
      if (!areaTotals[r.areaId]) {
        areaTotals[r.areaId] = {
          areaId: r.areaId,
          areaName: r.areaName,
          areaGroupName: r.areaGroupName,
          total: 0,
        };
      }
      areaTotals[r.areaId].total += 1;

      // University total
      if (!uniTotals[r.universityId]) {
        uniTotals[r.universityId] = {
          universityId: r.universityId,
          universityName: r.universityNameSnapshot,
          governorate: r.universityGovernorateNameSnapshot,
          total: 0,
        };
      }
      uniTotals[r.universityId].total += 1;

      // Cell
      const cellKey = `${r.areaId}__${r.universityId}`;
      if (!cells[cellKey]) {
        cells[cellKey] = {
          areaId: r.areaId,
          areaName: r.areaName,
          areaGroupName: r.areaGroupName,
          universityId: r.universityId,
          universityName: r.universityNameSnapshot,
          studentCount: 0,
          attendanceDemand: 0,
        };
      }
      cells[cellKey].studentCount += 1;
      cells[cellKey].attendanceDemand += r.attendanceDaysPerMonth || 16;
    });

    const sortedAreas = Object.values(areaTotals).sort((a, b) => b.total - a.total);
    const sortedUniversities = Object.values(uniTotals).sort((a, b) => b.total - a.total);

    return {
      areas: sortedAreas,
      universities: sortedUniversities,
      cells,
    };
  },

  /**
   * Export demand records to CSV string
   */
  async exportDemandToCsv(filter: StudentDemandFilter = {}): Promise<string> {
    const { records } = await this.queryDemandRecords(filter, 1, 10000);

    const headers = [
      'كود المعرف',
      'اسم الطالب',
      'رقم الهاتف',
      'المنطقة السكنية',
      'حي / قطاع المنطقة',
      'الجامعة المطلوبة',
      'المحافظة',
      'أيام الحضور شهرياً',
      'حالة الطلب',
      'حالة التخطيط التشغيلي',
      'تاريخ التسجيل',
      'مسجل بواسطة',
      'الملاحظات',
    ];

    const rows = records.map((r) => [
      `"${r.id}"`,
      `"${r.studentName.replace(/"/g, '""')}"`,
      `"${r.phone}"`,
      `"${r.areaName.replace(/"/g, '""')}"`,
      `"${r.areaGroupName.replace(/"/g, '""')}"`,
      `"${r.universityNameSnapshot.replace(/"/g, '""')}"`,
      `"${r.universityGovernorateNameSnapshot.replace(/"/g, '""')}"`,
      `"${r.attendanceDaysPerMonth || ''}"`,
      `"${r.status === 'ACTIVE' ? 'نشط' : 'مؤرشف'}"`,
      `"${r.planningStatus}"`,
      `"${r.createdAt.split('T')[0]}"`,
      `"${(r.createdBy?.email || '').replace(/"/g, '""')}"`,
      `"${(r.notes || '').replace(/"/g, '""')}"`,
    ]);

    // UTF-8 BOM for Excel Arabic compatibility
    const bom = '\uFEFF';
    return bom + [headers.join(','), ...rows.map((row) => row.join(','))].join('\r\n');
  },

  /**
   * Purge all mock and dummy records to prepare the system for live production
   */
  async purgeAllMockRecords(): Promise<void> {
    if (typeof window !== 'undefined') {
      try {
        sessionStorage.removeItem(LOCAL_DEMAND_KEY);
        localStorage.removeItem(LOCAL_DEMAND_KEY);
        sessionStorage.removeItem('oto_dev_demand_requests');
        localStorage.removeItem('oto_dev_demand_requests');
        sessionStorage.removeItem('nashgo_dev_student_bookings');
        saveLocalDemandRecords([]);
      } catch {
        // Ignored
      }
    }
  },
};
